import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@apollo/client/react';
import {
  GlobalSearchDocument,
  type GlobalSearchQuery,
  type OrganizationFilterInput,
  type SubscriptionFilterInput,
  type LicenceFilterInput,
  type OrganizationContactFilterInput,
  type OrganizationAddressFilterInput,
  type StringOperationFilterInput,
  type IntOperationFilterInput,
} from '@/generated/root-types';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const LIMIT = 8;
const MAX_HITS = 24;

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * API `contains` is case-sensitive. We OR several shapes (original, lower, upper, title)
 * so e.g. "sub" also matches stored "SUB-…".
 */
function caseSearchVariants(raw: string): string[] {
  const t = raw.trim();
  if (!t) return [];
  const out = new Set<string>();
  out.add(t);
  out.add(t.toLowerCase());
  out.add(t.toUpperCase());
  if (t.length > 0) {
    out.add(t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
  }
  return [...out];
}

function containsIgnoreCase(q: string): StringOperationFilterInput {
  const variants = caseSearchVariants(q);
  if (variants.length === 0) return { contains: q };
  if (variants.length === 1) return { contains: variants[0]! };
  return { or: variants.map((v) => ({ contains: v })) };
}

function exactIntSearch(q: string): IntOperationFilterInput | undefined {
  const t = q.trim();
  if (!/^\d+$/.test(t)) return undefined;

  const value = Number(t);
  if (!Number.isSafeInteger(value)) return undefined;

  return { eq: value };
}

export type GlobalSearchHit = {
  key: string;
  label: string;
  secondary: string;
  category: 'organization' | 'subscription' | 'licence' | 'contact' | 'address';
  to: string;
};

export default function GlobalSearchBar() {
  const { t } = useTranslation();
  const theme = useTheme();
  const onPrimary = theme.palette.primary.contrastText;
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const debounced = useDebouncedValue(input.trim(), DEBOUNCE_MS);
  const active = debounced.length >= MIN_CHARS;

  const orgWhere: OrganizationFilterInput | undefined = useMemo(() => {
    if (!active) return undefined;
    const q = debounced;
    const tenantNumber = exactIntSearch(q);
    return {
      or: [
        { customerNumber: containsIgnoreCase(q) },
        { name: containsIgnoreCase(q) },
        { description: containsIgnoreCase(q) },
        { tenant: { name: containsIgnoreCase(q) } },
        ...(tenantNumber ? [{ tenant: { tenantNumber } }] : []),
      ],
    };
  }, [active, debounced]);

  const subWhere: SubscriptionFilterInput | undefined = useMemo(() => {
    if (!active) return undefined;
    const q = debounced;
    const tenantNumber = exactIntSearch(q);
    return {
      or: [
        { number: containsIgnoreCase(q) },
        { tenant: { name: containsIgnoreCase(q) } },
        ...(tenantNumber ? [{ tenant: { tenantNumber } }] : []),
        { organizationUnit: { name: containsIgnoreCase(q) } },
        { organizationUnit: { organization: { customerNumber: containsIgnoreCase(q) } } },
      ],
    };
  }, [active, debounced]);

  const licWhere: LicenceFilterInput | undefined = useMemo(() => {
    if (!active) return undefined;
    const q = debounced;
    const tenantNumber = exactIntSearch(q);
    return {
      or: [
        { serial: containsIgnoreCase(q) },
        { deviceUid: containsIgnoreCase(q) },
        { deviceInfo: containsIgnoreCase(q) },
        { deviceDetails: containsIgnoreCase(q) },
        { deviceBrand: containsIgnoreCase(q) },
        { deviceModel: containsIgnoreCase(q) },
        { tenant: { name: containsIgnoreCase(q) } },
        ...(tenantNumber ? [{ tenant: { tenantNumber } }] : []),
        { organizationUnit: { name: containsIgnoreCase(q) } },
        { organizationUnit: { organization: { customerNumber: containsIgnoreCase(q) } } },
        { additionalPropertiesJson: containsIgnoreCase(q) },
      ],
    };
  }, [active, debounced]);

  const contactWhere: OrganizationContactFilterInput | undefined = useMemo(() => {
    if (!active) return undefined;
    const q = debounced;
    const tenantNumber = exactIntSearch(q);
    return {
      or: [
        { firstName: containsIgnoreCase(q) },
        { lastName: containsIgnoreCase(q) },
        { email: containsIgnoreCase(q) },
        { organization: { customerNumber: containsIgnoreCase(q) } },
        ...(tenantNumber ? [{ tenant: { tenantNumber } }] : []),
      ],
    };
  }, [active, debounced]);

  const addressWhere: OrganizationAddressFilterInput | undefined = useMemo(() => {
    if (!active) return undefined;
    const q = debounced;
    const tenantNumber = exactIntSearch(q);
    return {
      or: [
        { phoneNumber: containsIgnoreCase(q) },
        { addressLine1: containsIgnoreCase(q) },
        { city: containsIgnoreCase(q) },
        { zipCode: containsIgnoreCase(q) },
        { email: containsIgnoreCase(q) },
        { organization: { customerNumber: containsIgnoreCase(q) } },
        ...(tenantNumber ? [{ tenant: { tenantNumber } }] : []),
      ],
    };
  }, [active, debounced]);

  const searchVariables = useMemo(
    () => ({
      limit: LIMIT,
      orgWhere,
      subWhere,
      licWhere,
      contactWhere,
      addrWhere: addressWhere,
    }),
    [orgWhere, subWhere, licWhere, contactWhere, addressWhere],
  );

  const { data: searchData, loading: searchLoading } = useQuery<GlobalSearchQuery>(
    GlobalSearchDocument,
    {
      variables: searchVariables,
      skip: !active,
    },
  );

  const loading = active && searchLoading;

  const hits = useMemo((): GlobalSearchHit[] => {
    if (!active) return [];
    const out: GlobalSearchHit[] = [];

    for (const o of searchData?.organizations?.nodes ?? []) {
      out.push({
        key: `org-${o.id}`,
        label: o.name,
        secondary: [o.tenant?.name, t('globalSearch.categoryOrganization')]
          .filter(Boolean)
          .join(' · '),
        category: 'organization',
        to: `/customers/${o.id}`,
      });
    }
    for (const c of searchData?.organizationContacts?.nodes ?? []) {
      const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '–';
      out.push({
        key: `contact-${c.id}`,
        label: name,
        secondary: `${c.organization.name} · ${t('globalSearch.categoryContact')}`,
        category: 'contact',
        to: `/customers/${c.organization.id}`,
      });
    }
    for (const a of searchData?.organizationAddresses?.nodes ?? []) {
      const line = [a.phoneNumber, a.addressLine1, [a.zipCode, a.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(' · ');
      out.push({
        key: `addr-${a.id}`,
        label: line || a.organization.name,
        secondary: `${a.organization.name} · ${t('globalSearch.categoryAddress')}`,
        category: 'address',
        to: `/customers/${a.organization.id}`,
      });
    }
    for (const s of searchData?.subscriptions?.nodes ?? []) {
      out.push({
        key: `sub-${s.id}`,
        label: s.number,
        secondary: [
          s.organizationUnit?.name,
          s.tenant?.name,
          t('globalSearch.categorySubscription'),
        ]
          .filter(Boolean)
          .join(' · '),
        category: 'subscription',
        to: `/subscriptions/${s.id}`,
      });
    }
    for (const l of searchData?.licences?.nodes ?? []) {
      out.push({
        key: `lic-${l.id}`,
        label: l.serial,
        secondary: [l.tenant?.name, l.organizationUnit?.name, t('globalSearch.categoryLicence')]
          .filter(Boolean)
          .join(' · '),
        category: 'licence',
        to: `/licences/${l.id}`,
      });
    }

    return out.slice(0, MAX_HITS);
  }, [active, searchData, t]);

  const noOptionsText = useMemo(() => {
    if (!active) return t('globalSearch.minCharsHint');
    if (loading) return t('globalSearch.searching');
    return t('globalSearch.noResults');
  }, [active, loading, t]);

  return (
    <Autocomplete<GlobalSearchHit, false, false, true>
      sx={{ width: '100%', maxWidth: 440 }}
      freeSolo
      options={hits}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      inputValue={input}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') setInput(value);
        if (reason === 'clear') setInput('');
      }}
      loading={loading}
      loadingText={t('globalSearch.searching')}
      onChange={(_, value) => {
        if (value && typeof value !== 'string' && 'to' in value) {
          navigate(value.to);
          setInput('');
        }
      }}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('globalSearch.placeholder')}
          aria-label={t('globalSearch.ariaLabel')}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: alpha(onPrimary, 0.75) }} fontSize="small" aria-hidden />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} aria-hidden />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: alpha(onPrimary, 0.18),
              color: onPrimary,
              pl: 0.5,
              '& fieldset': { borderColor: alpha(onPrimary, 0.35) },
              '&:hover fieldset': { borderColor: alpha(onPrimary, 0.55) },
              '&.Mui-focused fieldset': { borderColor: alpha(onPrimary, 0.85) },
            },
            '& .MuiInputBase-input::placeholder': {
              color: alpha(onPrimary, 0.65),
              opacity: 1,
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key: _k, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & {
          key?: React.Key;
        };
        return (
          <li {...liProps} key={option.key}>
            <Box sx={{ display: 'block', py: 1.25, px: 0.5, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>
                  {option.label}
                </Typography>
                <Chip
                  size="small"
                  label={t(`globalSearch.categoryShort.${option.category}`)}
                  sx={{
                    height: 22,
                    flexShrink: 0,
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                noWrap
                sx={{ mt: 0.25 }}
              >
                {option.secondary}
              </Typography>
            </Box>
          </li>
        );
      }}
      slotProps={{
        popper: { sx: { zIndex: (theme) => theme.zIndex.modal } },
        listbox: { sx: { maxHeight: 360, py: 0 } },
      }}
    />
  );
}
